import React, { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import "moment/locale/es";
import {
  getFirestore,
  updateDoc,
  collection,
  doc,
  query,
  getDocs,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import Modal from "react-modal";
import Nav from "./Nav";
import ModalCreateEvent from "./ModalCreateEvent";
import VistaDia from "./VistaDia";
import VistaSemana from "./VistaSemana";
import VistaMes from "./VistaMes";
import VistaAño from "./VistaAño";
import Spinner from "./Spinner"; // Asegúrate de tener importado el Spinner

Modal.setAppElement("#root");

const CalendarioGd = () => {
  const [currentDate, setCurrentDate] = useState(moment());
  const [filters, setFilters] = useState([]); // Categorías dinámicas desde Firebase
  const [fsEvents, setFsEvents] = useState([]); // Cotizaciones, eventos y tareas
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState("Month view");
  const [proximasAVencer, setProximasAVencer] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(["Todos"]);
  const [loading, setLoading] = useState(true); // Estado para el spinner

  // Función para traer las categorías desde Firebase
  const fetchCategorias = useCallback(async () => {
    const firestore = getFirestore();
    const q = query(collection(firestore, "filtrosCategorias"));
    const snapshot = await getDocs(q);
    const categoriasData = snapshot.docs.map((doc) => ({
      id: doc.id,
      label: doc.data().label,
      color: doc.data().color || "#f0f0f0", // asume un color por defecto si no existe
    }));
    setFilters(categoriasData);
  }, []);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  const handleAddCategory = useCallback(
    async (newCategoryName, chosenColor) => {
      try {
        const firestore = getFirestore();
        // 1) Guardar en la colección 'filtrosCategorias'
        await addDoc(collection(firestore, "filtrosCategorias"), {
          label: newCategoryName,
          color: chosenColor || "#f0f0f0",
        });
        // 2) Volver a cargar categorías
        await fetchCategorias();
      } catch (error) {
        console.error("Error al crear nueva categoría:", error);
        Swal.fire("Error", "No se pudo crear la nueva categoría.", "error");
      }
    },
    [fetchCategorias]
  );

  // Función para traer los datos (cotizaciones, eventos y tareas)
  const fetchFirestoreData = useCallback(async () => {
    try {
      const firestore = getFirestore();

      // Cotizaciones
      const cotizacionesSnap = await getDocs(
        query(collection(firestore, "cotizaciones"))
      );
      const cotizaciones = cotizacionesSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: `cotizacion-${doc.id}`,
          title: `Cotización #${data.numeroCotizacion || "Sin número"}`,
          numeroCotizacion: data.numeroCotizacion || "Sin número",
          start: moment(data.fechaVencimiento).startOf("day").toDate(),
          end: moment(data.fechaVencimiento).endOf("day").toDate(),
          allDay: true,
          resource: "cotizacion",
          color: "#dbeafe",
          vence: moment(data.fechaVencimiento).format("DD/MM/YYYY"),
        };
      });
      const proximasCotizaciones = cotizaciones
        .filter((cotizacion) =>
          moment(cotizacion.start).isSameOrAfter(moment().startOf("day"))
        )
        .sort((a, b) => moment(a.start).diff(moment(b.start)))
        .slice(0, 6);
      setProximasAVencer(proximasCotizaciones);

      // Eventos
      const eventosSnap = await getDocs(
        query(collection(firestore, "eventos"))
      );
      const eventos = eventosSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: `evento-${doc.id}`,
          title: data.title,
          start: moment(data.from).toDate(),
          end: moment(data.to).toDate(),
          allDay: true,
          resource: "evento",
          color: "#dcfce7",
        };
      });

      // Tareas
      const tareasSnap = await getDocs(
        query(collection(firestore, "tareaCalendario"))
      );
      const tareas = tareasSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: `tarea-${doc.id}`,
          title: data.title,
          start: moment(data.from).toDate(),
          end: moment(data.to).toDate(),
          allDay: true,
          resource: "tarea",
          color: data.color || "#f0f0f0",
          category: data.category || null,
        };
      });

      setProximosEventos(eventos);
      setFsEvents([...cotizaciones, ...eventos, ...tareas]);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los datos.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false); // Finaliza la carga, sin importar el resultado
    }
  }, []);

  useEffect(() => {
    fetchFirestoreData();
  }, [fetchFirestoreData]);

  // Función para refrescar datos (para actualizaciones instantáneas)
  const refreshData = useCallback(async () => {
    setLoading(true);
    await fetchFirestoreData();
  }, [fetchFirestoreData]);

  // Función para generar días festivos
  const getFechasFestivas = useCallback((year) => {
    const fechasFestivasBase = [
      { title: "Año Nuevo", month: "01", day: "01", color: "#fee2e2" },
      {
        title: "Día de la Constitución",
        month: "02",
        day: "05",
        color: "#fee2e2",
      },
      {
        title: "Natalicio de Benito Juárez",
        month: "03",
        day: "21",
        color: "#fee2e2",
      },
      { title: "Día del Trabajo", month: "05", day: "01", color: "#fee2e2" },
      {
        title: "Independencia de México",
        month: "09",
        day: "16",
        color: "#fee2e2",
      },
      {
        title: "Revolución Mexicana",
        month: "11",
        day: "20",
        color: "#fee2e2",
      },
      { title: "Navidad", month: "12", day: "25", color: "#fee2e2" },
    ];
    return fechasFestivasBase.map((festivo) => ({
      id: `festivo-${festivo.title}`,
      title: festivo.title,
      start: moment(
        `${year}-${festivo.month}-${festivo.day}`,
        "YYYY-MM-DD"
      ).toDate(),
      end: moment(
        `${year}-${festivo.month}-${festivo.day}`,
        "YYYY-MM-DD"
      ).toDate(),
      allDay: true,
      resource: "festivo",
      color: festivo.color,
    }));
  }, []);

  const combinedEvents = useMemo(() => {
    const festivos = getFechasFestivas(currentDate.year());
    return [...festivos, ...fsEvents];
  }, [currentDate, fsEvents, getFechasFestivas]);

  const fixedFilterOptions = useMemo(() => {
    const base = ["Todos", "Festivos", "Eventos", "Cotizaciones"];
    const dynamic = filters
      .map((f) => f.label)
      .filter((label) => !base.includes(label));
    return [...base, ...dynamic];
  }, [filters]);

  const toggleFixedFilter = (filter) => {
    setSelectedFilters((prev) => {
      if (prev.includes(filter)) {
        return prev.filter((f) => f !== filter);
      } else {
        if (filter === "Todos") {
          return ["Todos"];
        } else {
          const newFilters = prev.filter((f) => f !== "Todos");
          return [...newFilters, filter];
        }
      }
    });
  };

  const filteredEvents = useMemo(() => {
    if (selectedFilters.length === 0 || selectedFilters.includes("Todos")) {
      return combinedEvents;
    }
    return combinedEvents.filter((entry) =>
      selectedFilters.some((filter) => {
        if (filter === "Eventos" && entry.resource === "evento") return true;
        if (filter === "Festivos" && entry.resource === "festivo") return true;
        if (filter === "Cotizaciones" && entry.resource === "cotizacion")
          return true;
        if (entry.resource === "tarea" && entry.category === filter)
          return true;
        return false;
      })
    );
  }, [selectedFilters, combinedEvents]);

  const openModal = useCallback(() => {
    fetchCategorias(); // Recargar categorías
    setModalIsOpen(true);
  }, [fetchCategorias]);

  const closeModal = useCallback(() => setModalIsOpen(false), []);

// Dentro de CalendarioGd
const handleSaveEvent = useCallback(
  async ({ id, title, date, category, color }) => {
    try {
      const firestore = getFirestore();
      const from = moment(date).toISOString();
      const to = moment(date).toISOString();

      // Si la categoría NO es "Eventos", se guarda como "tarea".
      // Si sí es "Eventos", se guarda como "evento".
      const newResource =
        category && category !== "Eventos" ? "tarea" : "evento";
      const newCategory = category && category !== "Eventos" ? category : null;

      // REVISAR SI ES EDICIÓN O CREACIÓN
      if (id) {
        // --- EDICIÓN ---
        // Determinamos si el doc es un evento o una tarea por el prefijo del id
        const oldResource = id.startsWith("evento-") ? "evento" : "tarea";
        const docId = id.replace(/^(evento-|tarea-)/, ""); // id real (sin prefijo)

        if (oldResource === newResource) {
          // 1) Si NO cambió el tipo, actualizamos en la misma colección
          const collectionName =
            oldResource === "evento" ? "eventos" : "tareaCalendario";
          const docRef = doc(firestore, collectionName, docId);

          await updateDoc(docRef, {
            title,
            from,
            to,
            color: color || "#f0f0f0",
            resource: newResource,
            category: newCategory,
          });
        } else {
          // 2) SI cambió de evento a tarea (o viceversa):
          //    Creamos en la nueva colección y Borramos el doc de la anterior
          const newCollection =
            newResource === "evento" ? "eventos" : "tareaCalendario";
          await addDoc(collection(firestore, newCollection), {
            title,
            from,
            to,
            color: color || "#f0f0f0",
            resource: newResource,
            category: newCategory,
          });

          // Borramos el viejo
          const oldCollection =
            oldResource === "evento" ? "eventos" : "tareaCalendario";
          const oldDocRef = doc(firestore, oldCollection, docId);
          await deleteDoc(oldDocRef);
        }
      } else {
        // --- CREACIÓN ---
        // No hay id => se crea en la colección correspondiente
        const collectionName =
          newResource === "evento" ? "eventos" : "tareaCalendario";
        await addDoc(collection(firestore, collectionName), {
          title,
          from,
          to,
          color: color || "#f0f0f0",
          resource: newResource,
          category: newCategory,
        });
      }

      Swal.fire({
        title: id ? "Evento actualizado" : "Evento creado",
        text: id
          ? "El evento se ha actualizado correctamente."
          : "El evento ha sido agregado.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Refrescamos datos para ver los cambios sin recargar
      await refreshData();
      closeModal();
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo guardar el evento/tarea.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  },
  [refreshData, closeModal]
);



  const handlePrev = useCallback(() => {
    switch (viewMode) {
      case "Day view":
        setCurrentDate((prev) => prev.clone().subtract(1, "day"));
        break;
      case "Week view":
        setCurrentDate((prev) => prev.clone().subtract(1, "week"));
        break;
      case "Month view":
        setCurrentDate((prev) => prev.clone().subtract(1, "month"));
        break;
      case "Year view":
        setCurrentDate((prev) => prev.clone().subtract(1, "year"));
        break;
      default:
        break;
    }
  }, [viewMode]);

  const handleNext = useCallback(() => {
    switch (viewMode) {
      case "Day view":
        setCurrentDate((prev) => prev.clone().add(1, "day"));
        break;
      case "Week view":
        setCurrentDate((prev) => prev.clone().add(1, "week"));
        break;
      case "Month view":
        setCurrentDate((prev) => prev.clone().add(1, "month"));
        break;
      case "Year view":
        setCurrentDate((prev) => prev.clone().add(1, "year"));
        break;
      default:
        break;
    }
  }, [viewMode]);

  const handleToday = useCallback(() => {
    setCurrentDate(moment());
  }, []);

  // Dentro de CalendarioGd:
  const handleDeleteCategory = useCallback(
    async (categoryId, label) => {
      try {
        // Evitar que se borren categorías fijas
        if (
          label === "Eventos" ||
          label === "Festivos" ||
          label === "Cotizaciones"
        ) {
          Swal.fire({
            title: "Operación no permitida",
            text: `No se puede eliminar la categoría "${label}".`,
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }

        const firestore = getFirestore();
        const docRef = doc(firestore, "filtrosCategorias", categoryId);

        // Confirmar la eliminación
        const { isConfirmed } = await Swal.fire({
          title: "¿Eliminar categoría?",
          text: `¿Estás seguro de que deseas eliminar la categoría "${label}"?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          scrollbarPadding: false,
        });

        if (isConfirmed) {
          await deleteDoc(docRef);
          await fetchCategorias(); // Recarga las categorías
          Swal.fire({
            title: "Categoría eliminada",
            text: `La categoría "${label}" se eliminó correctamente.`,
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            scrollbarPadding: false,
          });
        }
      } catch (error) {
        console.error("Error al eliminar la categoría:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo eliminar la categoría.",
          icon: "error",
          confirmButtonText: "OK",
          scrollbarPadding: false,
        });
      }
    },
    [fetchCategorias]
  );

  const renderFixedFilters = () => {
    return fixedFilterOptions.map((ff) => {
      const originalCategory = filters.find((cat) => cat.label === ff);

      return (
        <div key={ff} className="mb-3 w-40 flex flex-wrap items-start gap-2">
          {/* Checkbox */}
          <input
            type="checkbox"
            className="checkbox-select-all"
            checked={selectedFilters.includes(ff)}
            onChange={() => toggleFixedFilter(ff)}
          />

          {/* Texto de la categoría */}
          <span
            className={`
      font-medium 
      break-words 
      whitespace-normal 
      flex-1
      ${selectedFilters.includes(ff) ? "text-blue-600" : "text-custom-purple"}
    `}
          >
            {ff}
          </span>

          {/* Ícono de eliminar (solo si cumple condiciones) */}
          {ff !== "Eventos" &&
            ff !== "Festivos" &&
            ff !== "Cotizaciones" &&
            ff !== "Todos" &&
            selectedFilters.includes(ff) &&
            originalCategory && (
              <button
                onClick={() => handleDeleteCategory(originalCategory.id, ff)}
                className="text-gray-500 hover:text-gray-700 ml-auto self-end"
                title="Eliminar categoría"
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
        </div>
      );
    });
  };

  const renderView = () => {
    switch (viewMode) {
      case "Day view":
        return (
          <VistaDia
            events={filteredEvents}
            currentDate={currentDate}
            onChangeDate={setCurrentDate}
            onAddEvent={openModal}
            onSave={handleSaveEvent}
          />
        );
      case "Week view":
        return (
          <VistaSemana
            events={filteredEvents}
            currentDate={currentDate}
            onAddEvent={openModal}
            onSave={handleSaveEvent}
          />
        );
      case "Month view":
        return (
          <VistaMes
            events={filteredEvents}
            currentDate={currentDate}
            handleToday={handleToday}
            onSave={handleSaveEvent}
            filters={filters}
            refreshData={refreshData}
          />
        );
      case "Year view":
        return (
          <VistaAño
            events={filteredEvents}
            currentDate={currentDate}
            onSave={handleSaveEvent}
            filters={filters}
            refreshData={refreshData}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    // Mientras se cargan los datos, se muestra el Spinner (puedes personalizar el estilo)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
        events={combinedEvents}
      />
      <div className="tailwind-scope">
        <div className="flex">
          <div className="w-64 bg-gray-50 pl-14">
            <div className="flex justify-center mt-4">
              <button
                onClick={openModal}
                className="bg-blue-500 text-white font-medium py-2 px-4 rounded-md mb-4 hover:bg-blue-700"
              >
                + Agregar Nuevo
              </button>
            </div>
            <div className="ml-6 mt-5">
              <h3 className="text-gray-400 font-medium mb-5">FILTROS</h3>
              {renderFixedFilters()}
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-center mt-3 mb-3 bg-gray-50">
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  className="w-10 h-10 bg-gray-50 hover:bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="text-xl font-semibold text-gray-900 capitalize">
                  {(() => {
                    switch (viewMode) {
                      case "Day view":
                        return currentDate.format("DD MMMM YYYY");
                      case "Week view": {
                        const startOfWeek = currentDate.clone().startOf("week");
                        const endOfWeek = currentDate.clone().endOf("week");
                        return `Semana del ${startOfWeek.format(
                          "DD MMM"
                        )} al ${endOfWeek.format("DD MMM YYYY")}`;
                      }
                      case "Month view":
                        return currentDate.format("MMMM YYYY");
                      case "Year view":
                        return currentDate.format("YYYY");
                      default:
                        return currentDate.format("MMMM YYYY");
                    }
                  })()}
                </div>
                <button
                  onClick={handleNext}
                  className="w-10 h-10 bg-gray-50 hover:bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
              <div className="mr-6 flex items-center justify-center border rounded-lg bg-white divide-x divide-gray-200">
                {[
                  { label: "Año", value: "Year view" },
                  { label: "Mes", value: "Month view" },
                  { label: "Semana", value: "Week view" },
                  { label: "Día", value: "Day view" },
                ].map((view, index) => (
                  <button
                    key={view.value}
                    onClick={() => setViewMode(view.value)}
                    className={`px-6 py-2 text-sm font-medium w-full focus:outline-none transition-colors duration-200 ${
                      viewMode === view.value
                        ? "text-blue-600 bg-blue-100"
                        : "text-gray-700 hover:bg-gray-100"
                    } ${index === 0 ? "rounded-l-md" : ""} ${
                      index === 3 ? "rounded-r-md" : ""
                    }`}
                    style={{ minWidth: "100px", textAlign: "center" }}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
            </div>
            {renderView()}
            <ModalCreateEvent
              isOpen={modalIsOpen}
              onClose={closeModal}
              onSave={handleSaveEvent}
              categories={filters}
              onAddCategory={handleAddCategory}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarioGd;
